import { Modal as AntdModal } from 'antd'
import type { ReactNode } from 'react'
import './Modal.less'
import classnames from 'classnames'
import styled from 'styled-components'
import { Button } from '../../../general/Button'
import type { ButtonProps } from '../../../general/Button/Button'

export type ModalProps = {
  onConfirm?: () => void
  onCancel?: () => void
  setIsModalVisible?: (show: boolean) => void
  isModalVisible: boolean
  children: ReactNode
  localisation?: {
    title?: ReactNode
    description?: ReactNode
    confirmText: string
    cancelText?: string
  }
  confirmDisabled?: boolean
  fullScreen?: boolean
  isMobile?: boolean
  closeButton?: boolean
  smallPadding?: boolean
  classicPadding?: boolean
  width700px?: boolean
  width580px?: boolean
  extraFooterElement?: ReactNode
  closeOnConfirm?: boolean
  noPadding?: boolean
  confirmButtonProps?: ButtonProps & { disabledByPermission?: boolean }
  greyBackground?: boolean
  className?: string
  testId?: string
}

const ModalStyled = styled(AntdModal)`
  .ant-modal-content {
    padding: 27px !important;
    padding-bottom: 0px !important;
  }

  .ant-modal-content {
    padding: 0px;
  }

  .ant-modal-footer {
    padding-bottom: 15px;
  }
`

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ModalDescription = styled.div`
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  text-transform: none;
`

const Modal = ({
  onConfirm,
  onCancel,
  children,
  localisation,
  isModalVisible,
  setIsModalVisible,
  fullScreen,
  isMobile,
  confirmDisabled,
  closeButton = false,
  smallPadding = false,
  classicPadding = false,
  width700px = false,
  width580px = false,
  closeOnConfirm = true,
  // TODO
  noPadding = false,
  confirmButtonProps,
  extraFooterElement,
  greyBackground = false,
  className = '',
  testId
}: ModalProps) => {
  const modalTitle =
    localisation?.title || localisation?.description ? (
      <ModalTitle>
        {localisation?.title}
        {localisation?.description ? (
          <ModalDescription>{localisation.description}</ModalDescription>
        ) : null}
      </ModalTitle>
    ) : null

  const handleOk = () => {
    if (onConfirm) {
      onConfirm()
    }
    if (closeOnConfirm) {
      setIsModalVisible?.(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    setIsModalVisible?.(false)
  }

  const classNameWrapper = () =>
    classnames({
      'antd-modal-fullscreen': fullScreen,
      'antd-modal-not-fullscreen': !fullScreen,
      'ant-modal-unset-height': isMobile,
      'ant-modal-close-button': !closeButton,
      'antd-modal-small-padding': smallPadding,
      'antd-modal-classic-padding': classicPadding,
      'antd-modal-no-padding': noPadding,
      'antd-modal-700px-width': width700px,
      'antd-modal-580px-width': width580px,
      'antd-grey-background': greyBackground
    })

  return (
    <ModalStyled
      data-cy="modal-container"
      data-testid={testId ? `${testId}-modal` : 'modal'}
      className={`${classNameWrapper()} ${className}`}
      title={!fullScreen ? modalTitle : ''}
      open={isModalVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      maskClosable={false}
      footer={
        !fullScreen && onCancel && onConfirm
          ? [
              extraFooterElement && (
                <div
                  style={{ marginLeft: 0, marginRight: 'auto' }}
                  data-testid={
                    testId
                      ? `${testId}-modal-extra-footer`
                      : 'modal-extra-footer'
                  }
                >
                  {extraFooterElement}
                </div>
              ),
              localisation?.cancelText && (
                <Button
                  onClick={handleCancel}
                  variant="default"
                  data-cy="btn-cancel"
                  data-testid={
                    testId
                      ? `${testId}-modal-cancel-button`
                      : 'modal-cancel-button'
                  }
                >
                  {localisation?.cancelText}
                </Button>
              ),
              <Button
                disabled={confirmDisabled}
                onClick={handleOk}
                variant="primary"
                style={{ marginLeft: 10 }}
                {...confirmButtonProps}
                data-cy="btn-confirm"
                data-testid={
                  testId
                    ? `${testId}-modal-confirm-button`
                    : 'modal-confirm-button'
                }
              >
                {localisation?.confirmText}
              </Button>
            ]
          : []
      }
    >
      {children}
    </ModalStyled>
  )
}

export default Modal
